import { Route, Routes } from 'react-router-dom';

import { lazy, useEffect, useState } from 'react';
import Layout from '../Layout/Layout';
import RestrictedRoute from '../PrivateRoutes/RestrictedRoute/RestrictedRoute';
import PaidUserRoute from '../PrivateRoutes/PaidUserRoute/PaidUserRoute';
import { useRefresh } from '../../hooks/useRefresh';
import LoadPages from '../../pages/LoadPages/LoadPages';
import FreeUserRoute from '../PrivateRoutes/FreeUserRoute/FreeUserRoute';
import { usePreLoadData } from '../../hooks/usePreLoadData';
import FreeUserSoulmates from '../FreeUserComponents/FreeUserSoulmates/FreeUserSoulmates';
import AgencyUserRoute from '../PrivateRoutes/AgencyRoute/AgencyRoute';

const HomePage = lazy(() => import('../../pages/HomePage/HomePage'));
const CustomerSupport = lazy(() =>
  import('../mainComponents/CustomerSupport/CustomerSupport'),
);
const ManUserNews = lazy(() =>
  import('../ManUserComponents/ManUserNews/ManUserNews'),
);
const NotFoundPage = lazy(() =>
  import('../customComponents/NotFoundPage/NotFoundPage'),
);
const AgencyUserAnimationList = lazy(() =>
  import(
    '../AgencyUserComponents/AgencyUserAnimationList/AgencyUserAnimationList'
  ),
);
const AgencyUserAgenciesList = lazy(() =>
  import(
    '../AgencyUserComponents/AgencyUserAgenciesList/AgencyUserAgenciesList'
  ),
);
const AgencyUserVideoChatReports = lazy(() =>
  import(
    '../AgencyUserComponents/AgencyUserVideoChatReports/AgencyUserVideoChatReports'
  ),
);
const AgencyUserTextChatReports = lazy(() =>
  import(
    '../AgencyUserComponents/AgencyUserTextChatReports/AgencyUserTextChatReports'
  ),
);
const TestimonialList = lazy(() =>
  import('../../pages/TestimonialPage/TestimonialList/TestimonialList'),
);
const AgencyUserCreateTestimonial = lazy(() =>
  import(
    '../AgencyUserComponents/AgencyUserCreateTestimonial/AgencyUserCreateTestimonial'
  ),
);
const AgencyUserAddNewGift = lazy(() =>
  import('../AgencyUserComponents/AgencyUserAddNewGift/AgencyUserAddNewGift'),
);
const AgencyUserPaidUsersList = lazy(() =>
  import(
    '../AgencyUserComponents/AgencyUserPaidUsersList/AgencyUserPaidUsersList'
  ),
);
const AgencyUserCreateNews = lazy(() =>
  import('../AgencyUserComponents/AgencyUserCreateNews/AgencyUserCreateNews'),
);
const AgencyUserCreateVideoStory = lazy(() =>
  import(
    '../AgencyUserComponents/AgencyUserCreateVideoStory/AgencyUserCreateVideoStory'
  ),
);
const AgencyUserCreateUpdates = lazy(() =>
  import(
    '../AgencyUserComponents/AgencyUserCreateUpdates/AgencyUserCreateUpdates'
  ),
);
const AgencyUserAccount = lazy(() =>
  import('../AgencyUserComponents/AgencyUserAccount/AgencyUserAccount'),
);
const AgencyUserEditAccount = lazy(() =>
  import('../AgencyUserComponents/AgencyUserEditAccount/AgencyUserEditAccount'),
);
const AgencyUserBalance = lazy(() =>
  import('../AgencyUserComponents/AgencyUserBalance/AgencyUserBalance'),
);
const AgencyUserPayments = lazy(() =>
  import('../AgencyUserComponents/AgencyUserPayments/AgencyUserPayments'),
);
const AgencyUsersLettersStatistics = lazy(() =>
  import(
    '../AgencyUserComponents/AgencyUsersLettersStatistics/AgencyUsersLettersStatistics'
  ),
);
const AgencyUserFreeUsersList = lazy(() =>
  import(
    '../AgencyUserComponents/AgencyUserFreeUsersList/AgencyUserFreeUsersList'
  ),
);

const AgencyUserCreateNewUser = lazy(() =>
  import(
    '../AgencyUserComponents/AgencyUserCreateNewUser/AgencyUserCreateNewUser'
  ),
);
const AgencyUserUpdates = lazy(() =>
  import('../AgencyUserComponents/AgencyUserUpdates/AgencyUserUpdates'),
);
const ManUserUndelivered = lazy(() =>
  import('../ManUserComponents/ManUserUndelivered/ManUserUndelivered'),
);
const ManUserGiftDetails = lazy(() =>
  import('../ManUserComponents/ManUserGiftDetails/ManUserGiftDetails'),
);
const ManUserSearchUser = lazy(() =>
  import('../ManUserComponents/ManUserSearchUser/ManUserSearchUser'),
);
const AgencyUserPage = lazy(() =>
  import('../../pages/UserPages/AgencyUserPage/AgencyUserPage'),
);
const PassRecoveryPage = lazy(() =>
  import('../../pages/PassRecoveryPage/PassRecoveryPage'),
);

const OnlineDatingServicePage = lazy(() =>
  import('../../pages/OnlineDatingServicePage/OnlineDatingServicePage'),
);
const LiveChatServicePage = lazy(() =>
  import('../../pages/LiveChatServicePage/LiveChatServicePage'),
);
const GiftDeliveringPage = lazy(() =>
  import('../../pages/GiftDeliveringPage/GiftDeliveringPage'),
);

const CostsAndPricesPage = lazy(() =>
  import('../../pages/CostsAndPricesPage/CostsAndPricesPage'),
);
const NewLetter = lazy(() =>
  import('../mainComponents/Letters/NewLetter/NewLetter'),
);
const LettersTrash = lazy(() =>
  import('../mainComponents/Letters/LettersTrash/LettersTrash'),
);
const LettersSpam = lazy(() =>
  import('../mainComponents/Letters/LettersSpam/LettersSpam'),
);
const LettersPrimary = lazy(() =>
  import('../mainComponents/Letters/LettersPrimary/LettersPrimary'),
);
const LettersOutbox = lazy(() =>
  import('../mainComponents/Letters/LettersOutbox/LettersOutbox'),
);
const LettersInbox = lazy(() =>
  import('../mainComponents/Letters/LettersInbox/LettersInbox'),
);
const LetterPage = lazy(() =>
  import('../mainComponents/Letters/LetterPage/LetterPage'),
);
const ManUserProfile = lazy(() =>
  import('../ManUserComponents/ManUserProfile/ManUserProfile'),
);
const AboutUsPage = lazy(() =>
  import('../../pages/InformationPage/AboutUsPage/AboutUsPage'),
);
const ContactUsPage = lazy(() =>
  import('../../pages/InformationPage/ContactUsPage/ContactUsPage'),
);
const NewsPage = lazy(() => import('../../pages/NewsPage/NewsPage'));
const NewsDetailsPage = lazy(() =>
  import('../../pages/NewsDetailsPage/NewsDetailsPage'),
);

const VideoStoryPage = lazy(() =>
  import('../../pages/VideoStoryPage/VideoStoryPage'),
);
const VideoStoryDetailsPage = lazy(() =>
  import('../../pages/VideoStoryDetailsPage/VideoStoryDetailsPage'),
);

const TermsAndConditions = lazy(() =>
  import('../../pages/TermsAndConditions/TermsAndConditions'),
);
const SignUpPage = lazy(() => import('../../pages/SignUpPage/SignUpPage'));
const PrivacyPolicyPage = lazy(() =>
  import('../../pages/PrivacyPolicyPage/PrivacyPolicyPage'),
);
const RefundPolicyPage = lazy(() =>
  import('../../pages/RefundPolicyPage/RefundPolicyPage'),
);
const PartnershipPage = lazy(() =>
  import('../../pages/PartnershipPage/PartnershipPage'),
);
const TestimonialPage = lazy(() =>
  import('../../pages/TestimonialPage/TestimonialPage'),
);
const ManUserPage = lazy(() =>
  import('../../pages/UserPages/ManUserPage/ManUserPage'),
);
const ManUserUpdates = lazy(() =>
  import('../../components/ManUserComponents/ManUserUpdates/ManUserUpdates'),
);
const LiveChat = lazy(() =>
  import('../../components/mainComponents/LiveChat/LiveChat'),
);
const ManUserEditProfile = lazy(() =>
  import(
    '../../components/ManUserComponents/ManUserEditProfile/ManUserEditProfile'
  ),
);
const ManUserPurchaseCoins = lazy(() =>
  import(
    '../../components/ManUserComponents/ManUserPurchaseCoins/ManUserPurchaseCoins'
  ),
);
const ManUserExpenses = lazy(() =>
  import('../../components/ManUserComponents/ManUserExpenses/ManUserExpenses'),
);
const ManUserStatistics = lazy(() =>
  import(
    '../../components/ManUserComponents/ManUserStatistics/ManUserStatistics'
  ),
);
const ManUserNotifications = lazy(() =>
  import(
    '../../components/ManUserComponents/ManUserNotifications/ManUserNotifications'
  ),
);

const ManUserSoulmates = lazy(() =>
  import('../ManUserComponents/ManUserSoulmates/ManUserSoulmates'),
);
const ManUserFans = lazy(() =>
  import('../ManUserComponents/ManUserFans/ManUserFans'),
);

const ManUserMatches = lazy(() =>
  import('../ManUserComponents/ManUserMatches/ManUserMatches'),
);
const ManUserGiftsList = lazy(() =>
  import('../ManUserComponents/ManUserGiftsList/ManUserGiftsList'),
);
const ManUserOrders = lazy(() =>
  import('../ManUserComponents/ManUserOrders/ManUserOrders'),
);
const ManUserDeliveries = lazy(() =>
  import('../ManUserComponents/ManUserDeliveries/ManUserDeliveries'),
);
const ManUserRefusals = lazy(() =>
  import('../ManUserComponents/ManUserRefusals/ManUserRefusals'),
);
const FreeUserPage = lazy(() =>
  import('../../pages/UserPages/FreeUserPage/FreeUserPage'),
);

const FreeUserCreatePost = lazy(() =>
  import(
    '../../components/FreeUserComponents/FreeUserCreatePost/FreeUserCreatePost'
  ),
);
const FreeUserPrivateMedia = lazy(() =>
  import(
    '../../components/FreeUserComponents/FreeUserPrivateMedia/FreeUserPrivateMedia'
  ),
);
const FreeUserProfile = lazy(() =>
  import('../../components/FreeUserComponents/FreeUserProfile/FreeUserProfile'),
);
const UserProfilePage = lazy(() =>
  import('../../pages/UserProfilePage/UserProfilePage'),
);

const App = () => {
  const [isLoading, setIsLoading] = useState(false);
  usePreLoadData();
  useRefresh();
  useEffect(() => {
    // setTimeout(() => {
    //   setIsLoading(false);
    // }, 500);
  }, []);
  if (isLoading) {
    return <LoadPages />;
  }
  return (
    <Layout>
      <Routes>
        <Route
          path="/"
          element={<RestrictedRoute component={<HomePage />} />}
        />

        <Route path="/info">
          <Route path="terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="refund-policy" element={<RefundPolicyPage />} />
          <Route path="partnership" element={<PartnershipPage />} />
          <Route path="about-us" element={<AboutUsPage />} />
          <Route path="contact-us" element={<ContactUsPage />} />
        </Route>
        <Route
          path="/sign-up"
          element={
            <RestrictedRoute
              component={<SignUpPage />}
              redirectTo="/man-user/updates"
            />
          }
        />
        <Route path="*" element={<NotFoundPage />} />

        <Route path="/news" element={<NewsPage />} />
        <Route path="/news/:id" element={<NewsDetailsPage />} />

        <Route path="/testimonial/:id" element={<TestimonialPage />} />

        <Route path="/video-stories" element={<VideoStoryPage />} />
        <Route path="/video-stories/:id" element={<VideoStoryDetailsPage />} />

        <Route path="/costs-and-prices" element={<CostsAndPricesPage />} />
        <Route path="/gift-delivering" element={<GiftDeliveringPage />} />
        <Route path="/live-chat-service" element={<LiveChatServicePage />} />
        <Route
          path="/online-dating-service"
          element={<OnlineDatingServicePage />}
        />
        <Route path="/password-recovery" element={<PassRecoveryPage />} />

        <Route path="/user-profile/:id" element={<UserProfilePage />} />

        <Route
          path="/man-user"
          element={<PaidUserRoute component={<ManUserPage />} />}
        >
          <Route path="updates" element={<ManUserUpdates />} />
          <Route path="news" element={<ManUserNews />} />
          <Route path="user-profile/:id" element={<UserProfilePage />} />
          <Route path="live-chat" element={<LiveChat />} />
          <Route path="profile" element={<ManUserProfile />} />
          <Route path="edit-profile" element={<ManUserEditProfile />} />
          <Route path="purchase-coins" element={<ManUserPurchaseCoins />} />
          <Route path="expenses" element={<ManUserExpenses />} />
          <Route path="statistics" element={<ManUserStatistics />} />
          <Route path="notifications" element={<ManUserNotifications />} />

          <Route path="letters/new-letter" element={<NewLetter />} />
          <Route path="letters/inbox" element={<LettersInbox />} />
          <Route path="letters/:id" element={<LetterPage />} />
          <Route path="letters/outbox" element={<LettersOutbox />} />
          <Route path="letters/primary" element={<LettersPrimary />} />
          <Route path="letters/spam" element={<LettersSpam />} />
          <Route path="letters/trash" element={<LettersTrash />} />

          <Route path="customer-support" element={<CustomerSupport />} />
          <Route path="online-soulmates" element={<ManUserSoulmates />} />
          <Route path="fans" element={<ManUserFans />} />
          <Route path="matches" element={<ManUserMatches />} />
          <Route path="search-soulmates" element={<ManUserSearchUser />} />

          <Route path="gifts-list" element={<ManUserGiftsList />} />
          <Route path="gifts-list/:id" element={<ManUserGiftDetails />} />
          <Route path="orders" element={<ManUserOrders />} />
          <Route path="delivered" element={<ManUserDeliveries />} />
          <Route path="declined" element={<ManUserRefusals />} />
          <Route path="undelivered" element={<ManUserUndelivered />} />
        </Route>

        {/*    ===========================================   Free User  Page */}

        <Route
          path="/free-user"
          element={<FreeUserRoute component={<FreeUserPage />} />}
        >
          <Route path="user-profile/:id" element={<UserProfilePage />} />
          <Route path="create-post" element={<FreeUserCreatePost />} />
          <Route path="private-media" element={<FreeUserPrivateMedia />} />
          <Route path="live-chat" element={<LiveChat />} />
          <Route path="profile" element={<FreeUserProfile />} />

          <Route path="letters/new-letter" element={<NewLetter />} />
          <Route path="letters/inbox" element={<LettersInbox />} />
          <Route path="letters/:id" element={<LetterPage />} />
          <Route path="letters/outbox" element={<LettersOutbox />} />
          <Route path="letters/primary" element={<LettersPrimary />} />
          <Route path="letters/spam" element={<LettersSpam />} />
          <Route path="letters/trash" element={<LettersTrash />} />

          <Route path="online-soulmates" element={<FreeUserSoulmates />} />
          <Route path="fans" element={<ManUserFans />} />
          <Route path="matches" element={<ManUserMatches />} />
          <Route path="gifts-list" element={<ManUserGiftsList />} />
        </Route>
        {/*    ===========================================   Agency User  Page */}
        <Route
          path="/agency-user"
          element={<AgencyUserRoute component={<AgencyUserPage />} />}
        >
          <Route path="user-profile/:id" element={<UserProfilePage />} />
          <Route path="updates" element={<AgencyUserUpdates />} />

          <Route path="create-new-user" element={<AgencyUserCreateNewUser />} />
          <Route path="free-users-list" element={<AgencyUserFreeUsersList />} />

          <Route path="letters/new-letter" element={<NewLetter />} />
          <Route path="letters/inbox" element={<LettersInbox />} />
          <Route path="letters/:id" element={<LetterPage />} />
          <Route path="letters/outbox" element={<LettersOutbox />} />
          <Route path="letters/primary" element={<LettersPrimary />} />
          <Route path="letters/spam" element={<LettersSpam />} />
          <Route path="letters/trash" element={<LettersTrash />} />
          <Route
            path="letters/statistics"
            element={<AgencyUsersLettersStatistics />}
          />

          <Route path="customer-support" element={<CustomerSupport />} />

          <Route path="payments" element={<AgencyUserPayments />} />
          <Route path="balance" element={<AgencyUserBalance />} />

          <Route
            path="text-chat-reports"
            element={<AgencyUserTextChatReports />}
          />
          <Route
            path="video-chat-reports"
            element={<AgencyUserVideoChatReports />}
          />
          <Route path="animation-list" element={<AgencyUserAnimationList />} />

          <Route path="gifts-list" element={<ManUserGiftsList />} />
          <Route path="gifts-list/:id" element={<ManUserGiftDetails />} />
          <Route path="orders" element={<ManUserOrders />} />
          <Route path="delivered" element={<ManUserDeliveries />} />
          <Route path="declined" element={<ManUserRefusals />} />
          <Route path="undelivered" element={<ManUserUndelivered />} />

          <Route
            path="edit-agency-account/:id"
            element={<AgencyUserEditAccount />}
          />
          <Route path="agency-account/:id" element={<AgencyUserAccount />} />
          <Route path="agencies-list" element={<AgencyUserAgenciesList />} />

          <Route path="create-updates" element={<AgencyUserCreateUpdates />} />
          <Route path="man-user/updates" element={<ManUserUpdates />} />
          <Route path="man-user/news" element={<ManUserNews />} />

          <Route
            path="create-video-story"
            element={<AgencyUserCreateVideoStory />}
          />
          <Route path="video-stories-list" element={<VideoStoryPage />} />

          <Route path="create-news" element={<AgencyUserCreateNews />} />
          <Route path="news-list" element={<NewsPage />} />

          <Route
            path="create-testimonial"
            element={<AgencyUserCreateTestimonial />}
          />
          <Route path="testimonial-list" element={<TestimonialList />} />

          <Route path="paid-users-list" element={<AgencyUserPaidUsersList />} />

          <Route path="add-new-gift" element={<AgencyUserAddNewGift />} />
        </Route>
      </Routes>
    </Layout>
  );
};

export default App;
